import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaPlay, FaTimes, FaExpand, FaImages } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import '../styles/MediaGallery.css';

/**
 * Resolve an asset filename to its public URL.
 * Files live in public/assets/ and are served at /assets/...
 */
function getAssetSrc(fileName) {
  return `${process.env.PUBLIC_URL}/assets/${fileName}`;
}

function MediaGallery({ assets = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { language } = useLanguage();
  const tGallery = translations[language].projectDisplay;
  const conceptLabel = tGallery.conceptArt;

  // Localized caption when available; generated art is labeled as concept work
  const captionFor = (asset) => {
    const text = (asset.descriptions && asset.descriptions[language]) || asset.description;
    return asset.provenance === 'generated' ? `${conceptLabel} — ${text}` : text;
  };

  const current = assets[activeIndex];
  const total = assets.length;

  const goTo = useCallback((idx) => {
    setActiveIndex((idx + total) % total);
  }, [total]);

  const prev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);
  const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);

  // Keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, prev, next]);

  if (!assets.length) return null;

  const src = getAssetSrc(current.fileName);

  return (
    <div className="media-gallery">
      {/* ── Main Viewer ────────────────────────────────── */}
      <div className="gallery-viewer">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            className="gallery-main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {current.type === 'video' ? (
              <video
                src={src}
                controls
                className="gallery-video"
                preload="metadata"
              />
            ) : (
              <img
                src={src}
                alt={captionFor(current)}
                className="gallery-image"
                onClick={() => setLightboxOpen(true)}
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation arrows */}
        {total > 1 && (
          <>
            <button className="gallery-arrow gallery-arrow-left" onClick={prev} aria-label={tGallery.galleryPrev}>
              <FaChevronLeft />
            </button>
            <button className="gallery-arrow gallery-arrow-right" onClick={next} aria-label={tGallery.galleryNext}>
              <FaChevronRight />
            </button>
          </>
        )}

        {/* Counter + expand */}
        <div className="gallery-overlay-info">
          <span className="gallery-counter">
            <FaImages /> {activeIndex + 1} / {total}
          </span>
          {current.type === 'image' && (
            <button className="gallery-expand-btn" onClick={() => setLightboxOpen(true)} aria-label={tGallery.galleryExpand}>
              <FaExpand />
            </button>
          )}
        </div>
      </div>

      {/* ── Caption ────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        <motion.p
          key={activeIndex}
          className="gallery-caption"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {captionFor(current)}
        </motion.p>
      </AnimatePresence>

      {/* ── Thumbnail strip ────────────────────────────── */}
      {total > 1 && (
        <div className="gallery-thumbs">
          {assets.map((asset, idx) => {
            const thumbSrc = getAssetSrc(asset.fileName);
            return (
              <button
                key={asset.fileName}
                className={`gallery-thumb ${idx === activeIndex ? 'active' : ''}`}
                onClick={() => goTo(idx)}
                aria-label={`${tGallery.galleryView} ${idx + 1}`}
              >
                {asset.type === 'video' ? (
                  <div className="thumb-video-indicator">
                    <video src={thumbSrc} muted preload="metadata" />
                    <span className="thumb-play"><FaPlay /></span>
                  </div>
                ) : (
                  <img src={thumbSrc} alt={captionFor(asset)} loading="lazy" decoding="async" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Lightbox ───────────────────────────────────── */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            className="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxOpen(false)}
          >
            <button className="lightbox-close" onClick={() => setLightboxOpen(false)} aria-label={tGallery.galleryClose}>
              <FaTimes />
            </button>

            {total > 1 && (
              <>
                <button className="lightbox-arrow lightbox-arrow-left" onClick={(e) => { e.stopPropagation(); prev(); }} aria-label={tGallery.galleryPrev}>
                  <FaChevronLeft />
                </button>
                <button className="lightbox-arrow lightbox-arrow-right" onClick={(e) => { e.stopPropagation(); next(); }} aria-label={tGallery.galleryNext}>
                  <FaChevronRight />
                </button>
              </>
            )}

            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              {current.type === 'video' ? (
                <video src={src} controls autoPlay className="lightbox-video" />
              ) : (
                <img src={src} alt={captionFor(current)} className="lightbox-image" />
              )}
              <p className="lightbox-caption">{captionFor(current)}</p>
            </div>

            <span className="lightbox-counter">{activeIndex + 1} / {total}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MediaGallery;
